<div class="email-panel">
  <!-- Header -->
  <div class="panel-header">
    <h2 class="text-2xl font-bold">Email Templates</h2>
    <button 
      (click)="openAddForm()" 
      class="btn-primary"
      [disabled]="isLoading">
      <i class='bx bx-plus'></i> Add Template
    </button>
  </div>

  <!-- Messages -->
  <div *ngIf="errorMessage" class="alert alert-error">
    <i class='bx bx-error-circle'></i>
    {{ errorMessage }}
  </div>
  <div *ngIf="successMessage" class="alert alert-success">
    <i class='bx bx-check-circle'></i>
    {{ successMessage }}
  </div>

  <!-- Search and Filter -->
  <div class="search-filter-bar">
    <div class="search-box">
      <i class='bx bx-search'></i>
      <input 
        type="text" 
        placeholder="Search templates..." 
        [(ngModel)]="searchTerm"
        (ngModelChange)="onSearchChange()"
        class="search-input">
    </div>
    <div class="filter-box">
      <label>Filter by Date:</label>
      <input 
        type="date" 
        [(ngModel)]="filterByDate"
        (ngModelChange)="onDateFilterChange()"
        class="date-input">
      <button
        *ngIf="filterByDate"
        type="button"
        class="clear-date-btn"
        (click)="clearDateFilter()"
        title="Clear date filter">
        <i class='bx bx-x'></i>
      </button>
    </div>
  </div>

  <!-- Form Modal -->
  <div *ngIf="showForm" class="modal-overlay" (click)="closeForm()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>{{ isEditMode ? 'Edit Template' : 'Create New Template' }}</h3>
        <button class="close-btn" (click)="closeForm()">
          <i class='bx bx-x'></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="form-grid">
          <!-- Basic Info -->
          <div class="form-group">
            <label>Name <span class="required">*</span></label>
            <input 
              type="text" 
              [(ngModel)]="formData.name"
              class="form-input">
          </div>

          <div class="form-group">
            <label>Subject <span class="required">*</span></label>
            <input 
              type="text" 
              [(ngModel)]="formData.emailSubject"
              class="form-input">
          </div>

          <div class="form-group">
            <label>Template File (VM file) <span class="required">*</span></label>
            <div class="template-file-row">
              <input
                type="text"
                [(ngModel)]="formData.emailTemplateName"
                disabled
                class="form-input">
              <label class="file-upload-btn">
                <i class='bx bx-upload'></i>
                Upload
                <input
                  type="file"
                  accept=".vm,text/plain"
                  (change)="onTemplateFileSelected($event)">
              </label>
            </div>
            <div *ngIf="selectedTemplateFile" class="selected-file">
              <div>
                <i class='bx bx-file'></i>
                <span>{{ selectedTemplateFile.name }}</span>
              </div>
              <button type="button" (click)="removeTemplateFile()" title="Remove selected file">
                <i class='bx bx-x'></i>
              </button>
            </div>
          </div>

          <div class="form-group form-col-2">
            <label>Email Context <span class="required">*</span></label>
            <textarea
              [(ngModel)]="formData.emailText"
              rows="8"
              spellcheck="false"
              class="form-input"
              placeholder='{"title1":"...","title2":"...","title3":"...","content1":"...","content2":"..."}'></textarea>
          </div>

        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-secondary" (click)="closeForm()" [disabled]="isLoading">
          Cancel
        </button>
        <button class="btn-primary" (click)="saveTemplate()" [disabled]="isLoading">
          <span *ngIf="!isLoading">{{ isEditMode ? 'Update' : 'Create' }} Template</span>
          <span *ngIf="isLoading" class="flex items-center gap-2">
            <i class='bx bx-loader-alt animate-spin'></i>
            Saving...
          </span>
        </button>
      </div>
    </div>
  </div>

  <!-- Templates List -->
  <div class="templates-list">
    <div *ngIf="isLoading && templates.length === 0" class="loading-state">
      <i class='bx bx-loader-alt animate-spin text-3xl'></i>
      <p>Loading templates...</p>
    </div>

    <div *ngIf="!isLoading && filteredTemplates.length === 0" class="empty-state">
      <i class='bx bx-inbox text-4xl'></i>
      <p>No templates found</p>
    </div>

    <div class="templates-grid">
      <div *ngFor="let template of filteredTemplates" class="template-card">
        <div class="card-header">
          <h4 class="card-title">{{ template.name }}</h4>
          <span class="card-date">{{ template.itsDayDate }}</span>
        </div>

        <div class="card-content">
          <div class="info-item">
            <span class="label">Subject:</span>
            <span class="value">{{ template.emailSubject }}</span>
          </div>
          <div class="info-item">
            <span class="label">Template File:</span>
            <span class="value">{{ template.emailTemplateName }}</span>
          </div>
          <div class="info-item">
            <span class="label">Email Context:</span>
            <span class="value">{{ getTemplateContext(template) | slice:0:90 }}...</span>
          </div>
        </div>

        <div class="card-actions">
          <a
            class="action-btn download-btn"
            [href]="getTemplateDownloadUrl(template)"
            target="_blank"
            rel="noopener noreferrer"
            download
            title="Download template">
            <i class='bx bx-download'></i>
          </a>
          <label
            class="action-btn upload-btn"
            title="Upload template file">
            <i class='bx bx-upload'></i>
            <input
              type="file"
              accept=".vm,text/plain"
              (change)="onExistingTemplateFileSelected($event, template)">
          </label>
          <button 
            class="action-btn view-btn" 
            (click)="viewTemplate(template)"
            title="View details">
            <i class='bx bx-show'></i>
          </button>
          <button 
            class="action-btn edit-btn" 
            (click)="openEditForm(template)"
            title="Edit template">
            <i class='bx bx-edit'></i>
          </button>
          <button 
            class="action-btn delete-btn" 
            (click)="deleteTemplate(template)"
            title="Delete template">
            <i class='bx bx-trash'></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Dialog -->
  <div *ngIf="showDeleteDialog" class="modal-overlay" (click)="cancelDelete()">
    <div class="modal-content small-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Confirm Delete</h3>
        <button class="close-btn" (click)="cancelDelete()">
          <i class='bx bx-x'></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="delete-confirmation">
          <i class='bx bx-error-circle warning-icon'></i>
          <p>Are you sure you want to delete the template <strong>"{{ templateToDelete?.name }}"</strong>?</p>
          <p class="warning-text">This action cannot be undone.</p>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-secondary" (click)="cancelDelete()" [disabled]="isLoading">
          Cancel
        </button>
        <button class="btn-danger" (click)="confirmDelete()" [disabled]="isLoading">
          <span *ngIf="!isLoading">Delete Template</span>
          <span *ngIf="isLoading" class="flex items-center gap-2">
            <i class='bx bx-loader-alt animate-spin'></i>
            Deleting...
          </span>
        </button>
      </div>
    </div>
  </div>

  <!-- View Template Dialog -->
  <div *ngIf="showViewDialog" class="modal-overlay" (click)="closeViewDialog()">
    <div class="modal-content large-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Template Details</h3>
        <button class="close-btn" (click)="closeViewDialog()">
          <i class='bx bx-x'></i>
        </button>
      </div>

      <div class="modal-body" *ngIf="templateToView">
        <div class="template-details">
          <div class="detail-section">
            <h4>Basic Information</h4>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="label">Name:</span>
                <span class="value">{{ templateToView.name }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Subject:</span>
                <span class="value">{{ templateToView.emailSubject }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Template File:</span>
                <span class="value">{{ templateToView.emailTemplateName }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Date:</span>
                <span class="value">{{ templateToView.itsDayDate }}</span>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h4>Email Context</h4>
            <div class="detail-grid">
              <div class="detail-item full-width">
                <div class="content-value">{{ getTemplateContext(templateToView) }}</div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h4>Metadata</h4>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="label">Created:</span>
                <span class="value">{{ templateToView.createdOn }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Modified:</span>
                <span class="value">{{ templateToView.modifiedOn }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-primary" (click)="closeViewDialog()">
          Close
        </button>
      </div>
    </div>
  </div>
</div>
